import { AssessmentEditState } from './useAssessmentState';

interface UseContentManagementProps {
  state: AssessmentEditState;
  setConfirmingVersionChange: (confirming: boolean) => void;
  saveSettings: () => Promise<boolean>;
  saveContent: () => Promise<boolean>;
}

export function useContentManagement({ 
  state, 
  setConfirmingVersionChange, 
  saveSettings, 
  saveContent 
}: UseContentManagementProps) {
  
  // Check if content is being changed
  const isContentChanged = (): boolean => {
    if (!state.assessment) return false;
    
    // Simple check for now - if categories are different
    const originalJSON = JSON.stringify(state.assessment.categories);
    const currentJSON = JSON.stringify(state.categories);
    
    return originalJSON !== currentJSON;
  };

  // Main save handler that determines what type of save to perform
  // `userInitiated` MUST be true to open the version confirmation dialog.
  // This prevents accidental/implicit calls (e.g. from nested form submits) from triggering version prompts.
  const handleSubmit = async (options?: { userInitiated?: boolean }): Promise<boolean> => {
    const userInitiated = options?.userInitiated === true;

    console.log('[DEBUG handleSubmit] Called', {
      status: state.assessment?.status,
      isContentChanged: isContentChanged(),
      userInitiated,
      stack: new Error().stack
    });

    // If assessment is in draft mode, we can save everything without versioning
    if (!state.assessment || state.assessment.status === 'draft') {
      const settingsSaved = await saveSettings();
      const contentSaved = await saveContent();
      return settingsSaved && contentSaved;
    }

    // For published assessments, check if content changes need versioning
    if (isContentChanged()) {
      if (userInitiated) {
        console.log('[DEBUG handleSubmit] Setting confirmingVersionChange=true');
        // Show version confirmation dialog before saving
        setConfirmingVersionChange(true);
      }
      // Don’t proceed with any save until the user explicitly confirms via the dialog
      return false;
    }

    // If no content changes, just save settings
    return await saveSettings();
  };

  // Cancel version change confirmation - keep local changes intact
  const cancelContentChanges = () => {
    setConfirmingVersionChange(false);
    // DO NOT reset categories - keep the user's local changes
    // The user should be able to continue editing after canceling
  };

  // Auto-save content for draft assessments only
  // For published assessments, do NOT auto-trigger version confirmation.
  // Version confirmation should only happen when user explicitly clicks "Update" button.
  const autoSaveContent = async () => {
    console.log('[DEBUG autoSaveContent] Called', {
      status: state.assessment?.status,
      stack: new Error().stack
    });
    if (state.assessment?.status === 'draft') {
      await saveContent();
    }
    // For published assessments, we do nothing here - let changes accumulate locally
    // The user will click "Update" button which calls handleSubmit to trigger version confirmation
  };

  return {
    isContentChanged,
    handleSubmit,
    cancelContentChanges,
    autoSaveContent,
  };
}
